import { FULL_DAY_CANONICAL_HOUR, wallHourToCanonicalHour } from "./planBuckets";

/** 0–23 wall-clock hour in Europe/Zurich for an absolute instant */
function zurichWallHourFromInstant(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const s = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Zurich",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
  const m = s.match(/^(\d{1,2})/);
  return m ? parseInt(m[1], 10) : null;
}

/** YYYY-MM-DD in Europe/Zurich for a Date instance */
export function calendarKeyZurich(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;
  if (!y || !m || !day) return null;
  return `${y}-${m}-${day}`;
}

/**
 * Find plan day index 0..n-1 matching calendar date, or null if outside window.
 */
export function findDayIndexForCalendarKey(dayDates, key) {
  if (!key || !Array.isArray(dayDates)) return null;
  for (let i = 0; i < dayDates.length; i++) {
    if (calendarKeyZurich(dayDates[i]) === key) return i;
  }
  return null;
}

/**
 * Resolve card's canonical plan hour (9, 12, 15, 18) from starts_at.
 * Returns null if no valid start time is available.
 */
export function resolveActivityCanonicalHour(activity) {
  if (!activity) return null;
  const startsRaw =
    activity.starts_at != null ? String(activity.starts_at).trim() : "";
  if (!startsRaw) return null;

  const d = new Date(startsRaw);
  if (Number.isNaN(d.getTime())) return null;

  const wh = zurichWallHourFromInstant(d);
  if (wh == null) return null;

  return wallHourToCanonicalHour(wh);
}

/**
 * @param {object} activity — card row with optional starts_at
 * @param {Date[]} dayDates — from useWeeklyPlan (14 days)
 * @returns {{ day: number, hour: number } | null} day + canonical hour, or null → open picker
 */
export function resolveActivityPlanSlot(activity, dayDates) {
  if (!activity || !Array.isArray(dayDates) || dayDates.length === 0) return null;
  const startsRaw = activity.starts_at != null ? String(activity.starts_at).trim() : "";
  if (!startsRaw) return null;

  const d = new Date(startsRaw);
  if (Number.isNaN(d.getTime())) return null;

  const key = calendarKeyZurich(d);
  if (!key) return null;

  const dayIndex = findDayIndexForCalendarKey(dayDates, key);
  if (dayIndex === null) return null;

  const hour = resolveActivityCanonicalHour(activity);
  if (hour == null) return null;

  return {
    day: dayIndex,
    hour,
  };
}

/**
 * Same calendar-day resolution as {@link resolveActivityPlanSlot}, but always uses
 * {@link FULL_DAY_CANONICAL_HOUR} so adds land in the single "Ganztag" storage slot.
 * Returns null if the activity has no valid date in the plan window.
 */
export function resolveActivityFullDayPlanSlot(activity, dayDates) {
  if (!activity || !Array.isArray(dayDates) || dayDates.length === 0) return null;
  const startsRaw =
    activity.starts_at != null ? String(activity.starts_at).trim() : "";
  if (!startsRaw) return null;

  const d = new Date(startsRaw);
  if (Number.isNaN(d.getTime())) return null;

  const key = calendarKeyZurich(d);
  if (!key) return null;

  const dayIndex = findDayIndexForCalendarKey(dayDates, key);
  if (dayIndex === null) return null;

  return {
    day: dayIndex,
    hour: FULL_DAY_CANONICAL_HOUR,
  };
}
